'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Track } from 'livekit-client';
import { AnimatePresence, motion } from 'motion/react';
import { VideoTrack, useLocalParticipant, useRoomContext, useTracks, useVoiceAssistant, } from '@livekit/components-react';
import { ActionBar } from '@/components/embed-popup/action-bar';
import { LottieVisualizer } from '@/components/embed-popup/lottie-visualizer';
import { Transcript } from '@/components/embed-popup/transcript';
import useChatAndTranscription from '@/hooks/use-chat-and-transcription';
import { useDebugMode } from '@/hooks/useDebug';
import { cn } from '@/lib/utils';
const TILE_TRANSITION = {
    type: 'spring',
    stiffness: 675,
    damping: 75,
    mass: 1,
};
const TranscriptMotion = motion.create(Transcript);
export function useLocalTrackRef(source) {
    const { localParticipant } = useLocalParticipant();
    const publication = localParticipant.getTrackPublication(source);
    const trackRef = useMemo(() => (publication ? { source, participant: localParticipant, publication } : undefined), [source, publication, localParticipant]);
    return trackRef;
}
function isAgentAvailable(agentState) {
    return agentState == 'listening' || agentState == 'thinking' || agentState == 'speaking';
}
export const PopupView = ({ appConfig, disabled, sessionStarted, onEmbedError, onCollapse, widgetConfig, ref, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    useDebugMode();
    const room = useRoomContext();
    const { state: agentState, audioTrack: agentAudioTrack, videoTrack: agentVideoTrack, } = useVoiceAssistant();
    const { isCameraEnabled, isScreenShareEnabled } = useLocalParticipant();
    const [screenShareTrack] = useTracks([Track.Source.ScreenShare]);
    const cameraTrack = useLocalTrackRef(Track.Source.Camera);
    const [chatOpen, setChatOpen] = useState(false);
    const { messages, send } = useChatAndTranscription();
    const { supportsChatInput, supportsVideoInput, supportsScreenShare } = appConfig;
    const capabilities = {
        supportsChatInput,
        supportsVideoInput,
        supportsScreenShare,
    };
    async function onSendMessage(message) {
        await send(message);
        return;
    }
    // If the agent hasn't connected after an interval,
    // then show an error - something must not be working
    useEffect(() => {
        if (!sessionStarted) {
            return;
        }
        const timeout = setTimeout(() => {
            if (!isAgentAvailable(agentState)) {
                const reason = agentState === 'connecting'
                    ? 'Agent did not join the room. '
                    : 'Agent connected but did not complete initializing. ';
                onEmbedError({
                    title: 'Session ended',
                    description: _jsx("p", { className: "w-full", children: reason }),
                });
            }
        }, 10000);
        return () => clearTimeout(timeout);
    }, [agentState, sessionStarted, room, onEmbedError]);
    return (_jsxs("div", { ref: ref, inert: disabled, className: "flex h-full w-full flex-col overflow-hidden", children: [_jsxs("div", { className: "flex shrink-0 items-center justify-between px-4 pt-3 pb-1", children: [_jsxs("div", { className: "flex items-center gap-2", children: [(widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.logoUrl) && (
                            // eslint-disable-next-line @next/next/no-img-element
                            _jsx("img", { src: widgetConfig.logoUrl, alt: "", className: "size-5 object-contain" })), (widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.agentDisplayName) && (_jsx("span", { className: "text-fg1 text-xs font-semibold", children: widgetConfig.agentDisplayName }))] }), onCollapse && (_jsx("button", { onClick: onCollapse, className: "bg-bg3 hover:bg-separator1 text-fg2 hover:text-fg0 flex size-7 cursor-pointer items-center justify-center rounded-full transition-colors", "aria-label": "Minimize widget", children: _jsx("svg", { width: "14", height: "14", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M3 8h10", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round" }) }) }))] }), _jsxs("div", { className: "relative flex min-h-0 shrink grow flex-col overflow-hidden", children: [_jsx(TranscriptMotion, { initial: {
                            y: 10,
                            opacity: 0,
                        }, animate: {
                            y: chatOpen ? 0 : 10,
                            opacity: chatOpen ? 1 : 0,
                        }, transition: {
                            type: 'spring',
                            duration: 0.5,
                            bounce: 0,
                        }, messages: messages }), _jsx(AnimatePresence, { children: !agentVideoTrack && (_jsx(motion.div, { initial: {
                                scale: 1,
                                left: '50%',
                                top: '50%',
                                translateX: '-50%',
                                translateY: '-50%',
                                transformOrigin: 'center top',
                            }, animate: {
                                left: chatOpen && (isCameraEnabled || isScreenShareEnabled) ? '39%' : '50%',
                                scale: chatOpen ? 0.275 : 1,
                                top: chatOpen ? '12px' : '50%',
                                translateY: chatOpen ? '0' : '-50%',
                                transformOrigin: chatOpen ? 'center top' : 'center center',
                            }, transition: TILE_TRANSITION, className: cn('bg-bg1 dark:bg-bg2 pointer-events-none absolute flex aspect-square w-64 items-center justify-center rounded-2xl border border-transparent transition-colors', chatOpen && 'border-separator1 dark:border-separator2 drop-shadow-2xl'), children: _jsx(LottieVisualizer, { state: agentState, audioTrack: agentAudioTrack, color: widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.themeColor, themeMode: widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.themeMode, className: "h-full w-full" }) }, "audio-visualizer")) }), _jsx(AnimatePresence, { children: agentVideoTrack && (_jsx(motion.div, { initial: {
                                maskImage: 'radial-gradient(circle, rgba(0, 0, 0, 1) 0, rgba(0, 0, 0, 1) 40px, transparent 40px)',
                                filter: 'blur(20px)',
                            }, animate: {
                                opacity: chatOpen ? 0 : 1,
                                maskImage: 'radial-gradient(circle, rgba(0, 0, 0, 1) 0, rgba(0, 0, 0, 1) 500px, transparent 500px)',
                                filter: 'blur(0px)',
                            }, transition: {
                                opacity: {
                                    ease: 'linear',
                                    duration: 0.2,
                                },
                                maskImage: {
                                    ease: 'linear',
                                    duration: 1,
                                },
                                filter: {
                                    ease: 'linear',
                                    duration: 1,
                                },
                            }, className: "border-separator1 dark:border-separator2 pointer-events-none absolute inset-1 drop-shadow-lg/20", children: _jsx(VideoTrack, { trackRef: agentVideoTrack, width: (_b = (_a = agentVideoTrack === null || agentVideoTrack === void 0 ? void 0 : agentVideoTrack.publication.dimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 0, height: (_d = (_c = agentVideoTrack === null || agentVideoTrack === void 0 ? void 0 : agentVideoTrack.publication.dimensions) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 0, className: "h-full rounded-[24px] bg-black object-cover" }) }, "avatar")) }), _jsx(AnimatePresence, { children: agentVideoTrack && chatOpen && (_jsx(motion.div, { initial: {
                                opacity: 0,
                                scale: 0.5,
                                left: isCameraEnabled || isScreenShareEnabled ? '39%' : '50%',
                                top: '12px',
                                translateX: '-50%',
                                transformOrigin: 'center top',
                            }, animate: {
                                opacity: 1,
                                scale: 1,
                                left: isCameraEnabled || isScreenShareEnabled ? '37.5%' : '50%',
                            }, transition: TILE_TRANSITION, className: "border-separator1 dark:border-separator2 pointer-events-none absolute drop-shadow-lg/20", children: _jsx(VideoTrack, { trackRef: agentVideoTrack, width: (_f = (_e = agentVideoTrack === null || agentVideoTrack === void 0 ? void 0 : agentVideoTrack.publication.dimensions) === null || _e === void 0 ? void 0 : _e.width) !== null && _f !== void 0 ? _f : 0, height: (_h = (_g = agentVideoTrack === null || agentVideoTrack === void 0 ? void 0 : agentVideoTrack.publication.dimensions) === null || _g === void 0 ? void 0 : _g.height) !== null && _h !== void 0 ? _h : 0, className: "aspect-square w-[70px] rounded-md bg-black object-cover" }) }, "audio-visualizer")) }), _jsx(AnimatePresence, { children: ((cameraTrack && isCameraEnabled) || (screenShareTrack && isScreenShareEnabled)) && (_jsx(motion.div, { initial: {
                                scale: 0.5,
                                opacity: 0,
                                right: '12px',
                                top: '346px',
                                transformOrigin: 'center bottom',
                            }, animate: {
                                scale: 1,
                                opacity: 1,
                                top: chatOpen ? '12px' : '346px',
                                right: chatOpen ? '106px' : '12px',
                                transformOrigin: chatOpen ? 'center top' : 'center bottom',
                            }, exit: {
                                scale: 0.5,
                                opacity: 0,
                            }, transition: TILE_TRANSITION, className: "border-separator1 dark:border-separator2 pointer-events-none absolute drop-shadow-lg/20", children: _jsx(VideoTrack, { trackRef: cameraTrack || screenShareTrack, width: (_l = (_k = (_j = (cameraTrack || screenShareTrack)) === null || _j === void 0 ? void 0 : _j.publication.dimensions) === null || _k === void 0 ? void 0 : _k.width) !== null && _l !== void 0 ? _l : 0, height: (_p = (_o = (_m = (cameraTrack || screenShareTrack)) === null || _m === void 0 ? void 0 : _m.publication.dimensions) === null || _o === void 0 ? void 0 : _o.height) !== null && _p !== void 0 ? _p : 0, className: "aspect-square w-[70px] rounded-md bg-black object-cover" }) }, "camera")) }), _jsx(motion.div, { className: "shrink-0", initial: {
                            opacity: 0,
                            translateY: 8,
                        }, animate: {
                            opacity: sessionStarted ? 1 : 0,
                            translateY: sessionStarted ? 0 : 8,
                        }, transition: {
                            delay: 0.5,
                        }, children: _jsx(ActionBar, { capabilities: capabilities, onSendMessage: onSendMessage, onChatOpenChange: setChatOpen }) })] })] }));
};
